import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type { Session } from "@supabase/supabase-js";
import {
  fetchMetrics,
  isoDate,
  listProSubscribers,
  manageTestAccounts,
  manageUser,
  rangePreset,
  refundAndroid,
  subscriberHistory,
  syncStoreDownloads,
  type DateRange,
  type Metrics,
  type ProSubscriber,
  type SubscriberEvent,
} from "./lib/api";
import { supabase } from "./lib/supabase";

function formatConversionPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value}%`;
}

function platformLabel(key: string): string {
  if (key === "unknown") return "platform unknown";
  if (key === "other") return "other";
  if (key === "ios") return "iOS";
  if (key === "android") return "Android";
  return key;
}

export default function Dashboard({ session }: { session: Session }) {
  const [range, setRange] = useState<DateRange>(() => rangePreset("30d"));
  const [draftFrom, setDraftFrom] = useState(range.from);
  const [draftTo, setDraftTo] = useState(range.to);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncingDownloads, setSyncingDownloads] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [dataView, setDataView] = useState<"production" | "sandbox">(
    "production",
  );

  async function refresh(next = range) {
    setLoading(true);
    setLoadError(null);
    try {
      setMetrics(await fetchMetrics(next));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  function applyPreset(
    preset: "today" | "yesterday" | "7d" | "30d" | "90d" | "all",
  ) {
    const next = rangePreset(preset);
    setDraftFrom(next.from);
    setDraftTo(next.to);
    setRange(next);
  }

  function applyCustom(e: FormEvent) {
    e.preventDefault();
    if (!draftFrom || !draftTo) return;
    setRange({ from: draftFrom, to: draftTo });
  }

  async function onSyncDownloads() {
    setSyncingDownloads(true);
    setSyncNote(null);
    try {
      const result = await syncStoreDownloads();
      const ios = result.ios?.ok
        ? `iOS OK (${result.ios.downloads ?? 0} units, ${result.ios.countries ?? 0} countries)`
        : `iOS failed: ${result.ios?.error ?? "unknown"}`;
      const android = result.android?.ok
        ? "Android OK"
        : `Android: ${result.android?.error ?? "not configured"}`;
      setSyncNote(`${ios}. ${android}`);
      await refresh();
    } catch (err) {
      setSyncNote(err instanceof Error ? err.message : String(err));
    } finally {
      setSyncingDownloads(false);
    }
  }

  // Known limitation: no FX conversion for non-NOK payers — show USD only and
  // leave NOK blank until multi-currency payouts need real handling (e.g. NA USD).
  function formatNok(amount: number | null | undefined): string | null {
    if (amount == null || Number.isNaN(Number(amount))) return null;
    return (
      Number(amount).toLocaleString("nb-NO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + " NOK"
    );
  }

  function formatUsdAndNok(
    usd: number | null | undefined,
    nok: number | null | undefined,
  ): string {
    const usdPart =
      typeof usd === "number" && !Number.isNaN(usd)
        ? `$${usd.toFixed(2)}`
        : "$0.00";
    const nokPart = formatNok(nok);
    return nokPart ? `${usdPart} · ${nokPart}` : usdPart;
  }

  const revenueLifetime = formatUsdAndNok(
    metrics?.subscriptions?.lifetime_revenue_usd,
    metrics?.subscriptions?.lifetime_revenue_nok,
  );
  const revenueRange = formatUsdAndNok(
    metrics?.subscriptions?.revenue_in_range_usd,
    metrics?.subscriptions?.revenue_in_range_nok,
  );

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <div className="brand">
            Meatmatics <span>Admin</span>
          </div>
          <div className="muted">{session.user.email}</div>
        </div>
        <div className="topbar-actions">
          <button
            className="btn"
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
          >
            Refresh
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => void supabase.auth.signOut()}
          >
            Sign out
          </button>
        </div>
      </header>

      <InstallHint />
      <AlertsPanel />

      <nav className="view-tabs" aria-label="Data view">
        <button
          type="button"
          className={`view-tab${dataView === "production" ? " is-active" : ""}`}
          onClick={() => setDataView("production")}
        >
          Production
        </button>
        <button
          type="button"
          className={`view-tab${dataView === "sandbox" ? " is-active" : ""}`}
          onClick={() => setDataView("sandbox")}
        >
          Test / Sandbox
        </button>
      </nav>

      {dataView === "sandbox" ? (
        <SandboxDataView metrics={metrics} loading={loading} />
      ) : (
        <>
      {metrics?.closed_testing && (
        <div
          className={`panel testing-banner ${
            metrics.closed_testing.complete ? "testing-done" : ""
          }`}
        >
          <h2>Android closed testing</h2>
          <div className="testing-row">
            <div className="testing-days">
              {metrics.closed_testing.complete
                ? "Done"
                : metrics.closed_testing.days_remaining}
            </div>
            <div>
              <div className="testing-label">{metrics.closed_testing.label}</div>
              <p className="muted" style={{ margin: "0.25rem 0 0" }}>
                14-day clock started{" "}
                {isoDate(new Date(metrics.closed_testing.start))} · ends{" "}
                {isoDate(new Date(metrics.closed_testing.end))} ·{" "}
                {metrics.closed_testing.days_elapsed}/
                {metrics.closed_testing.required_days} days elapsed
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="panel">
        <h2>Date range</h2>
        <div className="preset-row">
          <button className="btn" type="button" onClick={() => applyPreset("today")}>
            Today
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => applyPreset("yesterday")}
          >
            Yesterday
          </button>
          <button className="btn" type="button" onClick={() => applyPreset("7d")}>
            7d
          </button>
          <button className="btn" type="button" onClick={() => applyPreset("30d")}>
            30d
          </button>
          <button className="btn" type="button" onClick={() => applyPreset("90d")}>
            90d
          </button>
          <button className="btn" type="button" onClick={() => applyPreset("all")}>
            All
          </button>
        </div>
        <form className="date-range-form" onSubmit={applyCustom}>
          <div className="field">
            <label htmlFor="from">From</label>
            <input
              id="from"
              type="date"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="to">To</label>
            <input
              id="to"
              type="date"
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              required
            />
          </div>
          <button className="btn btn-primary" type="submit">
            Apply
          </button>
        </form>
        {metrics && (
          <p className="muted" style={{ marginBottom: 0, marginTop: "0.75rem" }}>
            Showing {isoDate(new Date(metrics.range.from))} →{" "}
            {isoDate(new Date(metrics.range.to))} · generated{" "}
            {new Date(metrics.generated_at).toLocaleString()}
          </p>
        )}
      </div>

      {loadError && <p className="error">{loadError}</p>}
      {loading && !metrics && <div className="loading">Loading metrics…</div>}

      {metrics && (
        <>
          {(metrics.data_quality_notes?.length ?? 0) > 0 && (
            <details className="panel notes-panel">
              <summary>About these numbers</summary>
              <ul className="notes-list">
                {metrics.data_quality_notes!.map((note) => (
                  <li key={note.slice(0, 48)}>{note}</li>
                ))}
              </ul>
            </details>
          )}

          <section className="grid">
            <Metric label="Users (all)" value={metrics.users.total} />
            <Metric
              label="Organic users"
              value={metrics.users.organic_count ?? "—"}
            />
            <Metric
              label="Test users"
              value={metrics.users.test_count ?? "—"}
            />
            <Metric label="Signups (range)" value={metrics.users.signups_in_range} />
            <Metric label="Active (range)" value={metrics.users.active_in_range} />
            <Metric label="Users w/ games" value={metrics.users.with_games} />
            <Metric label="Games (all)" value={metrics.games.total} />
            <Metric label="Games created" value={metrics.games.created_in_range} />
            <Metric label="Pro subs" value={metrics.subscriptions.active_count} />
            <Metric label="Revenue (range)" value={revenueRange} />
            <Metric label="Revenue (life)" value={revenueLifetime} />
          </section>

          {metrics.subscriptions.sync_event_count === 0 && (
            <div className="panel">
              <h2>RevenueCat sync</h2>
              <p className="muted" style={{ margin: 0 }}>
                No purchase events stored yet. RC <code>TEST</code> webhooks are
                acknowledged but not persisted — real purchases/renewals will appear
                here.
              </p>
            </div>
          )}

          <div className="two-col">
            <div className="panel">
              <h2>Top 5 Pro countries</h2>
              {metrics.subscriptions.top_countries.length === 0 ? (
                <p className="muted">
                  No active subscribers synced yet — fills in from RevenueCat
                  purchase events.
                </p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Country</th>
                      <th>Subs</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.subscriptions.top_countries.map((row, i) => (
                      <tr key={row.country}>
                        <td>{i + 1}</td>
                        <td>{row.country}</td>
                        <td>{row.count}</td>
                        <td>${row.revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="panel">
              <h2>Top 5 download countries</h2>
              <p className="muted" style={{ marginTop: 0 }}>
                {metrics.downloads.note}
              </p>
              <div className="row" style={{ marginBottom: 12 }}>
                <button
                  className="btn"
                  type="button"
                  onClick={() => void onSyncDownloads()}
                  disabled={syncingDownloads || loading}
                >
                  {syncingDownloads ? "Syncing…" : "Sync store downloads"}
                </button>
              </div>
              {syncNote ? <p className="muted">{syncNote}</p> : null}
              {metrics.downloads.top_countries.length === 0 ? (
                <p className="muted">
                  Status: <code>{metrics.downloads.status}</code>
                  {metrics.downloads.last_synced_at
                    ? ` · last sync ${metrics.downloads.last_synced_at}`
                    : null}
                </p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Country</th>
                      <th>Downloads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.downloads.top_countries.map((row, i) => (
                      <tr key={row.country}>
                        <td>{i + 1}</td>
                        <td>{row.country}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {metrics.downloads.by_platform &&
              Object.keys(metrics.downloads.by_platform).length > 0 ? (
                <p className="muted" style={{ marginBottom: 0 }}>
                  By store:{" "}
                  {Object.entries(metrics.downloads.by_platform)
                    .map(([k, v]) => `${k} ${v}`)
                    .join(" · ")}
                  {metrics.downloads.last_synced_at
                    ? ` · synced ${new Date(metrics.downloads.last_synced_at).toLocaleString()}`
                    : null}
                </p>
              ) : null}
            </div>
          </div>

          <div className="two-col">
            <div className="panel">
              <h2>Signups / day</h2>
              <SparkBars
                points={metrics.trends.signups_by_day.map((d) => ({
                  day: d.day,
                  value: d.count,
                  unit: "signup",
                }))}
              />
            </div>
            <div className="panel">
              <h2>Games created / day</h2>
              <SparkBars
                points={metrics.trends.games_by_day.map((d) => ({
                  day: d.day,
                  value: d.count,
                  unit: "game",
                }))}
              />
            </div>
          </div>

          <div className="panel">
            <h2>Revenue / day (USD, range)</h2>
            <SparkBars
              points={metrics.trends.revenue_by_day.map((d) => ({
                day: d.day,
                value: d.usd,
                format: (v) => `$${v.toFixed(2)}`,
              }))}
            />
          </div>

          <div className="two-col">
            <div className="panel">
              <h2>Users by provider</h2>
              <KvTable data={metrics.users.providers} />
              {metrics.users.by_platform && (
                <>
                  <h3>Users by platform</h3>
                  <p className="muted" style={{ marginTop: 0 }}>
                    From push-token registration (proxy). “Platform unknown” =
                    no token yet — often notification permission denied.
                  </p>
                  <table>
                    <thead>
                      <tr>
                        <th>Platform</th>
                        <th>Total</th>
                        <th>Organic</th>
                        <th>Test</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(["ios", "android", "unknown"] as const).map((key) => {
                        const row = metrics.users.by_platform?.[key] ?? {
                          total: 0,
                          organic: 0,
                          test: 0,
                        };
                        return (
                          <tr key={key}>
                            <td>{platformLabel(key)}</td>
                            <td>{row.total}</td>
                            <td>{row.organic}</td>
                            <td>{row.test}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            <div className="panel">
              <h2>Subscribers by store</h2>
              <KvTable data={metrics.subscriptions.by_store} />
              {metrics.subscriptions.by_platform && (
                <>
                  <h3>Subscribers by platform</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Platform</th>
                        <th>Paid</th>
                        <th>Organic</th>
                        <th>Test</th>
                        <th>Conv. %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(["ios", "android", "other"] as const).map((key) => {
                        const paid =
                          metrics.subscriptions.by_platform?.[key] ?? 0;
                        const split =
                          metrics.subscriptions.by_platform_test_organic?.[
                            key
                          ] ?? { organic: 0, test: 0 };
                        const conv =
                          key === "other"
                            ? null
                            : metrics.subscriptions.conversion_pct_by_platform?.[
                                key
                              ];
                        return (
                          <tr key={key}>
                            <td>{platformLabel(key)}</td>
                            <td>{paid}</td>
                            <td>{split.organic}</td>
                            <td>{split.test}</td>
                            <td>
                              {key === "other"
                                ? "—"
                                : formatConversionPct(conv)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="muted" style={{ marginBottom: 0 }}>
                    Conversion % = organic Pro ÷ organic users (test accounts
                    excluded server-side).
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="two-col">
            <div className="panel">
              <h2>Games by status</h2>
              <KvTable data={metrics.games.by_status} />
            </div>
            <div className="panel">
              <h2>RC events in range</h2>
              <KvTable data={metrics.subscriptions.events_by_type_in_range} />
            </div>
          </div>

          <div className="panel">
            <h2>Games by species</h2>
            <KvTable data={metrics.games.by_species} />
          </div>

          <ProSubscribersPanel />

          <div className="panel">
            <h2>Auth errors by platform (range)</h2>
            <KvTable data={metrics.auth_errors_by_platform} />
            <h3>Recent</h3>
            {metrics.auth_errors.length === 0 ? (
              <p className="muted">None in range.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Platform</th>
                    <th>Code</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.auth_errors.slice(0, 12).map((e) => (
                    <tr key={e.id}>
                      <td>{new Date(e.created_at).toLocaleString()}</td>
                      <td>{e.platform ?? "—"}</td>
                      <td>{e.error_code ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="panel">
            <h2>Recent RC events</h2>
            {(metrics.subscriptions.recent_events?.length ?? 0) === 0 ? (
              <p className="muted">None yet.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Type</th>
                    <th>Store</th>
                    <th>Country</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.subscriptions.recent_events.map((e) => (
                    <tr key={String(e.event_id)}>
                      <td>
                        {e.created_at
                          ? new Date(String(e.created_at)).toLocaleString()
                          : "—"}
                      </td>
                      <td>{String(e.event_type)}</td>
                      <td>{String(e.store ?? "—")}</td>
                      <td>{String(e.country_code ?? "—")}</td>
                      <td>
                        {e.price != null
                          ? `$${Number(e.price)} USD`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="panel">
            <h2>Feature requests ({metrics.feature_requests.total})</h2>
            {metrics.feature_requests.recent.length === 0 ? (
              <p className="muted">None yet.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Suggestion</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.feature_requests.recent.map((f) => (
                    <tr key={f.id}>
                      <td>{new Date(f.created_at).toLocaleString()}</td>
                      <td>{f.suggestion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="panel">
            <h2>External consoles</h2>
            <div className="links">
              <a href={metrics.links.revenuecat} target="_blank" rel="noreferrer">
                RevenueCat
              </a>
              <a
                href={metrics.links.app_store_connect}
                target="_blank"
                rel="noreferrer"
              >
                App Store Connect
              </a>
              <a href={metrics.links.play_console} target="_blank" rel="noreferrer">
                Google Play Console
              </a>
            </div>
          </div>

          <RefundPanel onDone={() => void refresh()} />
          <UserManagePanel onDone={() => void refresh()} />
          <TestAccountsPanel
            listedCount={metrics.test_accounts?.count}
            onDone={() => void refresh()}
          />

          <div className="panel">
            <h2>Admin action log</h2>
            {metrics.admin_actions.length === 0 ? (
              <p className="muted">No actions yet.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Target / order</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.admin_actions.map((a) => (
                    <tr key={String(a.id)}>
                      <td>{new Date(String(a.created_at)).toLocaleString()}</td>
                      <td>{String(a.action)}</td>
                      <td>{String(a.actor_email ?? "—")}</td>
                      <td>{String(a.target_email ?? a.order_id ?? "—")}</td>
                      <td>{String(a.result)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
}

function SandboxDataView({
  metrics,
  loading,
}: {
  metrics: Metrics | null;
  loading: boolean;
}) {
  const sandbox = metrics?.subscriptions?.sandbox;
  const rows = sandbox?.subscribers ?? [];

  return (
    <>
      <div className="panel sandbox-banner">
        <h2>Test / Sandbox data</h2>
        <p className="muted" style={{ marginBottom: 0 }}>
          RevenueCat sandbox purchases — test money, not real customers or
          revenue. Use this to verify License-Testing removals and sandbox
          webhook flow. Production metrics never include these rows.
        </p>
      </div>

      {loading && !metrics && <div className="loading">Loading…</div>}

      {metrics && (
        <>
          <section className="grid">
            <Metric
              label="Sandbox snapshots"
              value={sandbox?.snapshot_count ?? 0}
            />
            <Metric
              label="Sandbox “revenue” (not real)"
              value={`$${Number(sandbox?.lifetime_revenue_usd ?? 0).toFixed(2)}`}
            />
          </section>

          <div className="panel">
            <h2>Sandbox subscriber snapshots</h2>
            {rows.length === 0 ? (
              <p className="muted">No sandbox rows in current_subscribers.</p>
            ) : (
              <div className="table-scroll table-scroll-wide">
                <table>
                  <thead>
                    <tr>
                      <th>App user / email</th>
                      <th>Env</th>
                      <th>Status</th>
                      <th>Store</th>
                      <th>Country</th>
                      <th>LTV USD</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr
                        key={String(
                          s.app_user_id ?? s.supabase_user_id ?? Math.random(),
                        )}
                      >
                        <td>
                          <code>
                            {String(s.app_user_id ?? s.supabase_user_id ?? "—").slice(
                              0,
                              36,
                            )}
                          </code>
                        </td>
                        <td>
                          <span className="badge">{String(s.environment ?? "—")}</span>
                        </td>
                        <td>{String(s.status ?? "—")}</td>
                        <td>{String(s.store ?? "—")}</td>
                        <td>{String(s.country_code ?? "—")}</td>
                        <td>
                          ${Number(s.lifetime_revenue_usd ?? 0).toFixed(2)}
                        </td>
                        <td>
                          {s.updated_at
                            ? new Date(String(s.updated_at)).toLocaleString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ProSubscribersPanel includeSandbox />
        </>
      )}
    </>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}

function InstallHint() {
  const [deferred, setDeferred] = useState<{
    prompt: () => Promise<void>;
  } | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("admin_pwa_hint_dismissed") === "1";
    } catch {
      return false;
    }
  });
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    const nav = window.navigator as Navigator & { standalone?: boolean };
    const check = () => setStandalone(mq.matches || nav.standalone === true);
    check();
    mq.addEventListener?.("change", check);

    function onBip(e: Event) {
      e.preventDefault();
      const pe = e as Event & {
        prompt: () => Promise<void>;
        userChoice: Promise<{ outcome: string }>;
      };
      setDeferred({
        prompt: async () => {
          await pe.prompt();
          await pe.userChoice;
          setDeferred(null);
        },
      });
    }
    window.addEventListener("beforeinstallprompt", onBip);
    return () => {
      mq.removeEventListener?.("change", check);
      window.removeEventListener("beforeinstallprompt", onBip);
    };
  }, []);

  if (standalone || dismissed) return null;

  return (
    <div className="install-hint">
      <strong>Add to Home Screen</strong> for a phone app. On iPhone: Share → Add
      to Home Screen. On Android: browser menu → Install app
      {deferred ? ", or tap Install below" : ""}.
      <div className="row">
        {deferred ? (
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => void deferred.prompt()}
          >
            Install
          </button>
        ) : null}
        <button
          className="btn"
          type="button"
          onClick={() => {
            try {
              localStorage.setItem("admin_pwa_hint_dismissed", "1");
            } catch {
              /* ignore */
            }
            setDismissed(true);
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

const NTFY_TOPIC =
  (import.meta.env.VITE_NTFY_TOPIC as string | undefined)?.trim() ||
  "meatmatics-subs-b62d1f9558b0";

function AlertsPanel() {
  const [perm, setPerm] = useState<NotificationPermission | "unsupported">(
    () =>
      typeof Notification === "undefined"
        ? "unsupported"
        : Notification.permission,
  );
  const [listening, setListening] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    return () => {
      esRef.current?.close();
      esRef.current = null;
    };
  }, []);

  function topicUrl(path = "") {
    return `https://ntfy.sh/${encodeURIComponent(NTFY_TOPIC)}${path}`;
  }

  async function ensurePermission(): Promise<boolean> {
    if (typeof Notification === "undefined") {
      setPerm("unsupported");
      setNote("This browser does not support notifications.");
      return false;
    }
    let next = Notification.permission;
    if (next === "default") {
      next = await Notification.requestPermission();
    }
    setPerm(next);
    return next === "granted";
  }

  function showLocal(title: string, body: string) {
    try {
      const n = new Notification(title, {
        body,
        icon: "/icons/icon-192.png",
        badge: "/icons/icon-192.png",
        tag: `meatmatics-admin-${Date.now()}`,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      /* ignore */
    }
  }

  async function startListening() {
    setNote(null);
    const ok = await ensurePermission();
    if (!ok) {
      setNote("Notification permission denied — use the ntfy app instead.");
      return;
    }
    esRef.current?.close();
    const es = new EventSource(topicUrl("/sse"));
    esRef.current = es;
    es.onopen = () => {
      setListening(true);
      setNote("Listening for signup + sale alerts while this page is open.");
    };
    es.onerror = () => {
      setListening(false);
      setNote("Alert stream disconnected — tap Listen again, or use the ntfy app for background pushes.");
    };
    es.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as {
          title?: string;
          message?: string;
          event?: string;
        };
        if (msg.event === "open") return;
        showLocal(msg.title || "Meatmatics Admin", msg.message || "");
      } catch {
        /* ignore parse errors */
      }
    };
  }

  function stopListening() {
    esRef.current?.close();
    esRef.current = null;
    setListening(false);
    setNote("Stopped in-browser listening.");
  }

  async function sendTest() {
    setTesting(true);
    setNote(null);
    try {
      const res = await fetch("https://ntfy.sh/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic: NTFY_TOPIC,
          title: "🧪 Admin test alert",
          message: "Signup + sale notifications are wired. You are subscribed.",
          tags: ["white_check_mark"],
          priority: 3,
        }),
      });
      if (!res.ok) {
        throw new Error(`ntfy ${res.status}: ${await res.text()}`);
      }
      setNote("Test alert sent — check ntfy / this browser.");
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="panel">
      <h2>Live alerts</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Phone push on every <strong>new signup</strong> and{" "}
        <strong>Pro sale</strong> (also renewals, cancels, billing issues) via
        ntfy — same channel as RevenueCat + profile INSERT webhooks.
      </p>
      <div className="row" style={{ marginBottom: "0.65rem" }}>
        <a
          className="btn btn-primary"
          href={topicUrl()}
          target="_blank"
          rel="noreferrer"
        >
          Open topic
        </a>
        <a
          className="btn"
          href={`ntfy://${NTFY_TOPIC}`}
          rel="noreferrer"
        >
          Open in ntfy app
        </a>
        <button
          className="btn"
          type="button"
          disabled={testing}
          onClick={() => void sendTest()}
        >
          {testing ? "Sending…" : "Send test"}
        </button>
        {listening ? (
          <button className="btn" type="button" onClick={stopListening}>
            Stop listen
          </button>
        ) : (
          <button
            className="btn"
            type="button"
            onClick={() => void startListening()}
            disabled={perm === "unsupported"}
          >
            Listen here
          </button>
        )}
      </div>
      <p className="muted" style={{ marginBottom: 0 }}>
        Background on phone: install{" "}
        <a href="https://ntfy.sh/" target="_blank" rel="noreferrer">
          ntfy
        </a>{" "}
        → Subscribe to topic <code>{NTFY_TOPIC}</code>. “Listen here” only works
        while Admin is open
        {perm !== "unsupported" ? ` · permission: ${perm}` : null}.
      </p>
      {note ? <p className="muted">{note}</p> : null}
    </div>
  );
}

function formatPrice(
  price: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (price == null || Number.isNaN(Number(price))) return "—";
  const cur = currency?.trim() || "USD";
  return `${Number(price)} ${cur}`;
}

/** NOK LTV display: last local price only when currency is NOK — never FX-convert. */
function formatLtvNok(r: {
  last_price_in_purchased_currency?: number | null;
  currency?: string | null;
}): string {
  if (String(r.currency ?? "").toUpperCase() !== "NOK") return "—";
  const amt = r.last_price_in_purchased_currency;
  if (amt == null || Number.isNaN(Number(amt))) return "—";
  return (
    Number(amt).toLocaleString("nb-NO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " NOK"
  );
}

/** Prefer local store amount + currency; fall back to USD proceeds labeled USD. */
function formatEventPrice(e: {
  price?: number | null;
  price_in_purchased_currency?: number | null;
  currency?: string | null;
}): string {
  const local = e.price_in_purchased_currency;
  const cur = e.currency?.trim();
  const usd = e.price;
  if (local != null && !Number.isNaN(Number(local)) && cur) {
    if (usd != null && !Number.isNaN(Number(usd))) {
      return `${Number(local)} ${cur} (≈$${Number(usd)} USD)`;
    }
    return `${Number(local)} ${cur}`;
  }
  if (usd != null && !Number.isNaN(Number(usd))) {
    return `$${Number(usd)} USD`;
  }
  return "—";
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function ProSubscribersPanel({
  includeSandbox = false,
}: {
  includeSandbox?: boolean;
}) {
  const [rows, setRows] = useState<ProSubscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [sortKey, setSortKey] = useState<
    "purchased_at" | "lifetime_revenue_usd" | "email" | "status"
  >("purchased_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<ProSubscriber | null>(null);
  const [events, setEvents] = useState<SubscriberEvent[]>([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyErr, setHistoryErr] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setErr(null);
    try {
      const res = await listProSubscribers({ includeSandbox });
      setRows(res.subscribers ?? []);
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeSandbox]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = rows.filter((r) => {
      if (statusFilter !== "all" && (r.status ?? "") !== statusFilter) {
        return false;
      }
      if (platformFilter !== "all" && r.platform !== platformFilter) {
        return false;
      }
      if (!needle) return true;
      const hay = [
        r.email,
        r.app_user_id,
        r.supabase_user_id,
        r.product_id,
        r.country_code,
        r.status,
        r.platform,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });

    list = [...list].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortKey === "lifetime_revenue_usd") {
        return (Number(a.lifetime_revenue_usd) - Number(b.lifetime_revenue_usd)) *
          dir;
      }
      if (sortKey === "email") {
        return (a.email ?? "").localeCompare(b.email ?? "") * dir;
      }
      if (sortKey === "status") {
        return (a.status ?? "").localeCompare(b.status ?? "") * dir;
      }
      const ta = a.purchased_at ?? "";
      const tb = b.purchased_at ?? "";
      return ta.localeCompare(tb) * dir;
    });
    return list;
  }, [rows, q, statusFilter, platformFilter, sortKey, sortDir]);

  async function openHistory(row: ProSubscriber) {
    setSelected(row);
    setEvents([]);
    setHistoryErr(null);
    setHistoryBusy(true);
    try {
      const res = await subscriberHistory({
        supabase_user_id: row.supabase_user_id ?? undefined,
        app_user_id: row.app_user_id ?? undefined,
      });
      setEvents(res.events ?? []);
    } catch (error) {
      setHistoryErr(error instanceof Error ? error.message : String(error));
    } finally {
      setHistoryBusy(false);
    }
  }

  const statuses = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) if (r.status) s.add(r.status);
    return [...s].sort();
  }, [rows]);

  return (
    <div className="panel">
      <h2>
        Pro Subscribers ({filtered.length}/{rows.length})
        {includeSandbox ? " — incl. sandbox" : " — production"}
      </h2>
      <p className="muted" style={{ marginTop: 0 }}>
        From <code>current_subscribers</code> + auth emails
        {includeSandbox
          ? " (PRODUCTION + SANDBOX)."
          : " (PRODUCTION only)."}{" "}
        Click a row for RevenueCat event history. Client-side filter/sort only.
      </p>

      <div className="sub-filters">
        <div className="field">
          <label htmlFor="sub-q">Search</label>
          <input
            id="sub-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="email, product, country…"
          />
        </div>
        <div className="field">
          <label htmlFor="sub-status">Status</label>
          <select
            id="sub-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="sub-plat">Platform</label>
          <select
            id="sub-plat"
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="sub-sort">Sort</label>
          <select
            id="sub-sort"
            value={`${sortKey}:${sortDir}`}
            onChange={(e) => {
              const [k, d] = e.target.value.split(":") as [
                typeof sortKey,
                typeof sortDir,
              ];
              setSortKey(k);
              setSortDir(d);
            }}
          >
            <option value="purchased_at:desc">Purchased (newest)</option>
            <option value="purchased_at:asc">Purchased (oldest)</option>
            <option value="lifetime_revenue_usd:desc">Revenue (high)</option>
            <option value="lifetime_revenue_usd:asc">Revenue (low)</option>
            <option value="email:asc">Email (A–Z)</option>
            <option value="status:asc">Status</option>
          </select>
        </div>
        <button
          className="btn"
          type="button"
          disabled={loading}
          onClick={() => void reload()}
        >
          {loading ? "…" : "Refresh"}
        </button>
      </div>

      {err && <p className="error">{err}</p>}
      {loading && rows.length === 0 ? (
        <p className="muted">Loading subscribers…</p>
      ) : rows.length === 0 ? (
        <p className="muted">
          No purchase events stored yet. RC <code>TEST</code> webhooks are
          acknowledged but not persisted — real purchases/renewals will appear
          here.
        </p>
      ) : filtered.length === 0 ? (
        <p className="muted">No subscribers match these filters.</p>
      ) : (
        <>
          <div className="card-list">
            {filtered.map((r) => {
              const key = r.supabase_user_id ?? r.app_user_id ?? r.email ?? "";
              const isSelected =
                selected &&
                ((r.supabase_user_id &&
                  r.supabase_user_id === selected.supabase_user_id) ||
                  (r.app_user_id && r.app_user_id === selected.app_user_id));
              return (
                <button
                  key={key}
                  type="button"
                  className={`card-item${isSelected ? " is-selected" : ""}`}
                  onClick={() => void openHistory(r)}
                >
                  <div className="card-item-title">
                    {r.email ??
                      `${(r.app_user_id ?? "unknown").slice(0, 12)}…`}
                  </div>
                  <div className="card-item-meta">
                    <span className={`badge ${r.status ?? ""}`}>
                      {r.status ?? "—"}
                    </span>
                    <span className="badge">{r.environment ?? "—"}</span>
                    <span>{platformLabel(r.platform)}</span>
                    <span>
                      <strong>
                        $
                        {Number(r.lifetime_revenue_usd ?? 0).toFixed(2)}
                      </strong>{" "}
                      LTV
                    </span>
                    <span>{formatLtvNok(r)}</span>
                    <span>{r.country_code ?? "—"}</span>
                    <span>{formatWhen(r.purchased_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="table-scroll table-scroll-wide desktop-only">
            <table className="table-click">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Platform</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Env</th>
                  <th>Country</th>
                  <th>Purchased</th>
                  <th>Expires</th>
                  <th>LTV USD</th>
                  <th>LTV NOK</th>
                  <th>Renew</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const key =
                    r.supabase_user_id ?? r.app_user_id ?? r.email ?? "";
                  const isSelected =
                    selected &&
                    ((r.supabase_user_id &&
                      r.supabase_user_id === selected.supabase_user_id) ||
                      (r.app_user_id &&
                        r.app_user_id === selected.app_user_id));
                  return (
                    <tr
                      key={key}
                      className={isSelected ? "is-selected" : undefined}
                      onClick={() => void openHistory(r)}
                    >
                      <td>
                        {r.email ?? (
                          <span className="muted">
                            {(r.app_user_id ?? "—").slice(0, 10)}…
                          </span>
                        )}
                      </td>
                      <td>{platformLabel(r.platform)}</td>
                      <td>
                        <span className={`badge ${r.status ?? ""}`}>
                          {r.status ?? "—"}
                        </span>
                      </td>
                      <td>{r.product_id ?? "—"}</td>
                      <td>
                        {formatPrice(
                          r.last_price_in_purchased_currency,
                          r.currency,
                        )}
                        {r.last_price != null ? (
                          <span className="muted">
                            {" "}
                            (≈${Number(r.last_price)} USD)
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <span className="badge">
                          {r.environment ?? "—"}
                        </span>
                      </td>
                      <td>{r.country_code ?? "—"}</td>
                      <td>{formatWhen(r.purchased_at)}</td>
                      <td>{formatWhen(r.expiration_at)}</td>
                      <td>
                        ${Number(r.lifetime_revenue_usd ?? 0).toFixed(2)}
                      </td>
                      <td>{formatLtvNok(r)}</td>
                      <td>
                        {r.will_renew == null
                          ? "—"
                          : r.will_renew
                            ? "yes"
                            : "no"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selected && (
        <div className="history-block">
          <h3>
            History — {selected.email ?? selected.app_user_id ?? "subscriber"}
          </h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Status <code>{selected.status ?? "—"}</code>
            {selected.cancel_reason
              ? ` · cancel: ${selected.cancel_reason}`
              : null}
            {selected.expiration_reason
              ? ` · expire: ${selected.expiration_reason}`
              : null}
            {selected.entitlement_ids?.length
              ? ` · entitlements: ${selected.entitlement_ids.join(", ")}`
              : null}
            {" · "}
            <button
              className="btn"
              type="button"
              style={{ padding: "0.2rem 0.5rem" }}
              onClick={() => {
                setSelected(null);
                setEvents([]);
              }}
            >
              Close
            </button>
          </p>
          {historyBusy && <p className="muted">Loading events…</p>}
          {historyErr && <p className="error">{historyErr}</p>}
          {!historyBusy && events.length === 0 && !historyErr ? (
            <p className="muted">No matching subscription_events.</p>
          ) : null}
          {events.length > 0 && (
            <div className="table-scroll table-scroll-wide">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Type</th>
                    <th>Price</th>
                    <th>Offer</th>
                    <th>Trial conv.</th>
                    <th>Tax / commission</th>
                    <th>Store</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.event_id}>
                      <td>
                        {formatWhen(e.event_timestamp ?? e.created_at)}
                      </td>
                      <td>{e.event_type}</td>
                      <td>{formatEventPrice(e)}</td>
                      <td>{e.offer_code ?? e.presented_offering_id ?? "—"}</td>
                      <td>
                        {e.is_trial_conversion == null
                          ? "—"
                          : e.is_trial_conversion
                            ? "yes"
                            : "no"}
                      </td>
                      <td>
                        {[
                          e.tax_percentage != null
                            ? `tax ${e.tax_percentage}`
                            : null,
                          e.commission_percentage != null
                            ? `comm ${e.commission_percentage}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </td>
                      <td>{e.store ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KvTable({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <p className="muted">No data.</p>;
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Key</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k}>
              <td>{k}</td>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatSparkDate(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function SparkBars({
  points,
}: {
  points: Array<{
    day: string;
    value: number;
    format?: (v: number) => string;
    unit?: string;
  }>;
}) {
  const max = useMemo(
    () => Math.max(1, ...points.map((p) => p.value)),
    [points],
  );
  const sum = useMemo(
    () => points.reduce((s, p) => s + p.value, 0),
    [points],
  );
  const peak = useMemo(() => {
    if (points.length === 0) return null;
    return points.reduce((a, b) => (b.value > a.value ? b : a));
  }, [points]);

  if (points.length === 0) return <p className="muted">No data in range.</p>;

  const step = points.length > 60 ? Math.ceil(points.length / 60) : 1;
  const visible = points.filter((_, i) => i % step === 0);

  return (
    <div className="spark">
      <div className="spark-bars">
        {visible.map((p) => {
          const valueText = p.format
            ? p.format(p.value)
            : p.unit
              ? `${p.value} ${p.unit}${p.value === 1 ? "" : "s"}`
              : String(p.value);
          return (
            <div
              key={p.day}
              className="spark-bar"
              data-tooltip={`${formatSparkDate(p.day)} · ${valueText}`}
              style={{ height: `${Math.max(2, (p.value / max) * 100)}%` }}
            />
          );
        })}
      </div>
      <div className="spark-meta muted">
        {sum === 0
          ? "All zeros in this range"
          : `Peak ${
              peak?.format?.(peak.value) ?? peak?.value
            } · ${points.length} days`}
      </div>
    </div>
  );
}

function RefundPanel({ onDone }: { onDone: () => void }) {
  const [orderId, setOrderId] = useState("");
  const [revoke, setRevoke] = useState(true);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    setErr(null);
    if (confirm !== "REFUND") {
      setErr("Type REFUND in the confirmation box to proceed.");
      return;
    }
    setBusy(true);
    try {
      const res = await refundAndroid(orderId.trim(), revoke);
      setMsg(`Refunded ${res.order_id}`);
      setOrderId("");
      setConfirm("");
      onDone();
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h2>Android refund (Play)</h2>
      <p className="muted">
        Calls Google Play <code>orders.refund</code>. iOS is not available via API.
      </p>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="order">Play order ID</label>
          <input
            id="order"
            placeholder="GPA.xxxx-xxxx-xxxx-xxxxx"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={revoke}
              onChange={(e) => setRevoke(e.target.checked)}
            />{" "}
            Also revoke entitlement / cancel renewals
          </label>
        </div>
        <div className="confirm-box">
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="refund-confirm">Type REFUND to confirm</label>
            <input
              id="refund-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>
        {err && <p className="error">{err}</p>}
        {msg && <p className="ok">{msg}</p>}
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <button className="btn btn-danger" type="submit" disabled={busy}>
            {busy ? "Refunding…" : "Issue Android refund"}
          </button>
        </div>
      </form>
    </div>
  );
}

function UserManagePanel({ onDone }: { onDone: () => void }) {
  const [query, setQuery] = useState("");
  const [lookup, setLookup] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const isEmail = query.includes("@");

  async function doLookup(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setMsg(null);
    setLookup(null);
    setConfirm("");
    setBusy(true);
    try {
      setLookup(
        await manageUser(
          isEmail
            ? { action: "lookup", email: query.trim() }
            : { action: "lookup", user_id: query.trim() },
        ),
      );
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function doAction(action: "suspend" | "delete") {
    setErr(null);
    setMsg(null);
    const needed = action === "suspend" ? "SUSPEND" : "DELETE";
    if (confirm !== needed) {
      setErr(`Type ${needed} to confirm.`);
      return;
    }
    setBusy(true);
    try {
      const user = lookup?.user as { id?: string } | undefined;
      const res = await manageUser({
        action,
        user_id: user?.id,
        confirm: needed,
      });
      setMsg(`${action} OK for ${String(res.email ?? res.user_id)}`);
      setConfirm("");
      if (action === "delete") setLookup(null);
      else setLookup(await manageUser({ action: "lookup", user_id: user?.id }));
      onDone();
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h2>User management</h2>
      <form onSubmit={doLookup}>
        <div className="field">
          <label htmlFor="user-query">Email or user ID</label>
          <input
            id="user-query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            required
          />
        </div>
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "…" : "Look up"}
        </button>
      </form>
      {lookup && (
        <>
          <h3>Result</h3>
          <pre className="json">{JSON.stringify(lookup, null, 2)}</pre>
          <div className="confirm-box">
            <div className="field">
              <label htmlFor="user-confirm">
                Type SUSPEND or DELETE to enable the matching action
              </label>
              <input
                id="user-confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="row">
              <button
                className="btn"
                type="button"
                disabled={busy || confirm !== "SUSPEND"}
                onClick={() => void doAction("suspend")}
              >
                Suspend user
              </button>
              <button
                className="btn btn-danger"
                type="button"
                disabled={busy || confirm !== "DELETE"}
                onClick={() => void doAction("delete")}
              >
                Delete user
              </button>
            </div>
          </div>
        </>
      )}
      {err && <p className="error">{err}</p>}
      {msg && <p className="ok">{msg}</p>}
    </div>
  );
}

function parseEmailList(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.includes("@"));
}

function TestAccountsPanel({
  listedCount,
  onDone,
}: {
  listedCount?: number;
  onDone: () => void;
}) {
  const [emailsText, setEmailsText] = useState("");
  const [accounts, setAccounts] = useState<
    Array<{
      email: string;
      source?: string | null;
      note?: string | null;
      created_at?: string;
    }>
  >([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function reloadList() {
    const res = await manageTestAccounts({ action: "list" });
    setAccounts(res.accounts ?? []);
  }

  useEffect(() => {
    void reloadList().catch((error) => {
      setErr(error instanceof Error ? error.message : String(error));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(action: "add" | "remove") {
    setErr(null);
    setMsg(null);
    const emails = parseEmailList(emailsText);
    if (emails.length === 0) {
      setErr("Enter at least one email (comma/space/newline separated).");
      return;
    }
    setBusy(true);
    try {
      const res = await manageTestAccounts({ action, emails });
      setMsg(
        action === "add"
          ? `Added ${res.added ?? emails.length} email(s)`
          : `Removed ${res.removed ?? emails.length} email(s)`,
      );
      setEmailsText("");
      await reloadList();
      onDone();
    } catch (error) {
      setErr(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h2>Test accounts</h2>
      <p className="muted">
        Emails in <code>known_test_accounts</code> are excluded from organic
        conversion math. Metrics list count:{" "}
        <strong>{listedCount ?? accounts.length}</strong>. Manage via{" "}
        <code>admin-test-accounts</code> only (RLS blocks direct writes).
      </p>
      <div className="field">
        <label htmlFor="test-emails">Emails to add or remove</label>
        <textarea
          id="test-emails"
          rows={4}
          placeholder={"tester@example.com\nanother@example.com"}
          value={emailsText}
          onChange={(e) => setEmailsText(e.target.value)}
        />
      </div>
      <div className="row">
        <button
          className="btn btn-primary"
          type="button"
          disabled={busy}
          onClick={() => void run("add")}
        >
          {busy ? "…" : "Add"}
        </button>
        <button
          className="btn btn-danger"
          type="button"
          disabled={busy}
          onClick={() => void run("remove")}
        >
          Remove
        </button>
        <button
          className="btn"
          type="button"
          disabled={busy}
          onClick={() => {
            setErr(null);
            setMsg(null);
            void reloadList().catch((error) => {
              setErr(error instanceof Error ? error.message : String(error));
            });
          }}
        >
          Refresh list
        </button>
      </div>
      {err && <p className="error">{err}</p>}
      {msg && <p className="ok">{msg}</p>}
      {accounts.length === 0 ? (
        <p className="muted">No test emails listed yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Source</th>
              <th>Added</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.email}>
                <td>{a.email}</td>
                <td>{a.source ?? "—"}</td>
                <td>
                  {a.created_at
                    ? new Date(a.created_at).toLocaleDateString()
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
